import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Typography } from 'Components/atoms';
import {
    ACTION_NAME,
    ACTION_TARGET,
    ACTION_TRIGGERED,
    FAILED_ACTIONS,
    MICROSOFT,
} from 'Core-utils/constants/constants';
import MicrosoftLogo from 'Assets/icons/microsoftLogoSymbol.svg';
import { getServiceInfoByKey } from 'Core-utils/helpers/helpers';
import { CustomerActionSummaryDto, ServiceProfile } from '@ampsec/platform-client';
import { Dropdown } from 'react-bootstrap';
import { TbDotsVertical } from '@react-icons/all-files/tb/TbDotsVertical';
import { MutableRefObject } from 'react';
import { CUSTOM_ACTION_MENU_OPTIONS, CustomActionMenuValue } from './AgGridEngagementActions.config';

const CENTERED_CELL_CLASS = 'ag-engagement-actions-grid__metric-col';
const CENTERED_CELL_CLASS_H = `${CENTERED_CELL_CLASS} ag-engagement-actions-grid__metric-col--h-center`;
const ACTION_TARGET_CELL_CLASS = 'ag-engagement-actions-grid__action-target-col';

const ACTION_TARGET_CELL_STYLE = {
    textAlign: 'left',
} as const;

const ACTION_TARGET_HEADER_CLASS = 'ag-engagement-actions-grid__action-target-header';

type MetricCellProps = {
    value: number;
    isLink?: boolean;
    onClick?: () => void;
};

const MetricCell = ({ value, isLink = false, onClick }: MetricCellProps) => {
    const content = (
        <Typography
            variant="body5"
            color="text-high-emphasis"
            className="mb-0 ag-engagement-actions-grid__metric-value"
        >
            {value}
        </Typography>
    );

    if (isLink && onClick) {
        return (
            <div className="ag-engagement-actions-grid__metric-cell">
                <button
                    type="button"
                    className="btn btn-link p-0 border-0 ag-engagement-actions-grid__metric-link"
                    onClick={(event) => {
                        event.stopPropagation();
                        onClick();
                    }}
                >
                    {content}
                </button>
            </div>
        );
    }

    return <div className="ag-engagement-actions-grid__metric-cell">{content}</div>;
};

export interface GetEngagementActionsColumnsParams {
    serviceProfiles: ServiceProfile[];
    onNameClickRef: MutableRefObject<(row: CustomerActionSummaryDto) => void>;
    onFailedRunsClickRef: MutableRefObject<(row: CustomerActionSummaryDto) => void>;
    onMenuActionRef: MutableRefObject<(action: CustomActionMenuValue, row: CustomerActionSummaryDto) => void>;
}

export function getEngagementActionsColumns({
    serviceProfiles,
    onNameClickRef,
    onFailedRunsClickRef,
    onMenuActionRef,
}: GetEngagementActionsColumnsParams): ColDef<CustomerActionSummaryDto>[] {
    return [
        {
            colId: 'displayValue',
            headerName: ACTION_NAME,
            sortable: true,
            flex: 1,
            minWidth: 200,
            cellClass: CENTERED_CELL_CLASS,
            valueGetter: (params) => params.data?.displayValue,
            cellRenderer: (params: ICellRendererParams<CustomerActionSummaryDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <div className="ag-engagement-actions-grid__metric-cell">
                        <button
                            type="button"
                            className="btn btn-link p-0 border-0 text-start w-100 ag-engagement-flow__link"
                            onClick={(event) => {
                                event.stopPropagation();
                                onNameClickRef.current(row);
                            }}
                        >
                            <Typography variant="body5" className="ag-engagement-flow__link text-truncate d-block mb-0">
                                {row.displayValue}
                            </Typography>
                        </button>
                    </div>
                );
            },
        },
        {
            colId: 'serviceKey',
            headerName: ACTION_TARGET,
            headerClass: ACTION_TARGET_HEADER_CLASS,
            sortable: true,
            flex: 1,
            minWidth: 180,
            cellClass: ACTION_TARGET_CELL_CLASS,
            cellStyle: ACTION_TARGET_CELL_STYLE,
            cellRenderer: (params: ICellRendererParams<CustomerActionSummaryDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                const info = getServiceInfoByKey(serviceProfiles, row.serviceKey);
                const amplifierLogo = getServiceInfoByKey(serviceProfiles, 'Amplifier')?.logo;
                // Fallback for providers with a hardcoded (non-Contentful-backed) serviceKey, e.g. the
                // Microsoft/Intune reboot action — don't rely on a CMS `serviceProfile` entry existing.
                const isMicrosoft = row.serviceKey === MICROSOFT;
                const logo = info?.logo ?? (isMicrosoft ? MicrosoftLogo : amplifierLogo);
                const displayValue = info?.displayValue ?? (isMicrosoft ? MICROSOFT : 'Custom');

                return (
                    <span className="ag-engagement-actions-grid__action-target-cell d-inline-flex align-items-center gap-2">
                        {logo ? <img src={logo} height={20} width={20} alt="" /> : null}
                        <Typography variant="body5" color="text-high-emphasis" className="mb-0 text-truncate">
                            {displayValue}
                        </Typography>
                    </span>
                );
            },
        },
        {
            colId: 'triggered',
            headerName: ACTION_TRIGGERED,
            sortable: true,
            flex: 0.75,
            minWidth: 160,
            cellClass: CENTERED_CELL_CLASS_H,
            valueGetter: (params) => params.data?.recentActivity?.totalCount ?? 0,
            cellRenderer: (params: ICellRendererParams<CustomerActionSummaryDto>) => (
                <MetricCell value={Number(params.value ?? 0)} />
            ),
        },
        {
            colId: 'failedAttempts',
            headerName: FAILED_ACTIONS,
            sortable: true,
            flex: 0.75,
            minWidth: 140,
            cellClass: CENTERED_CELL_CLASS_H,
            valueGetter: (params) => params.data?.recentActivity?.failureCount ?? 0,
            cellRenderer: (params: ICellRendererParams<CustomerActionSummaryDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                const count = row.recentActivity?.failureCount ?? 0;
                return (
                    <MetricCell value={count} isLink={count > 0} onClick={() => onFailedRunsClickRef.current(row)} />
                );
            },
        },
        {
            colId: 'actions',
            headerName: '',
            sortable: false,
            flex: 0.35,
            minWidth: 56,
            maxWidth: 72,
            suppressHeaderMenuButton: true,
            cellClass: CENTERED_CELL_CLASS_H,
            cellRenderer: (params: ICellRendererParams<CustomerActionSummaryDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <div className="ag-engagement-actions-grid__metric-cell select-menu-wrapper d-flex justify-content-center">
                        <Dropdown>
                            <Dropdown.Toggle className="border-0 text-text-low-emphasis d-flex p-0">
                                <TbDotsVertical size={20} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="p-0 gap-2 bg-body-color border-text-medium-emphasis dropdown__custom-menu">
                                {CUSTOM_ACTION_MENU_OPTIONS.map((item) => (
                                    <Dropdown.Item
                                        key={item.id}
                                        className="dropdown__menu-item d-flex align-items-center gap-2 bodyFont"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onMenuActionRef.current(item.value, row);
                                        }}
                                    >
                                        {item.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                );
            },
        },
    ];
}
